import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AiAssistantV2Store } from '@bizzdesign/ai-assistant/data-access';
import { isNotBlank } from '@bizzdesign/bizzdesign-sdk';
import { AccessControlService } from '@bizzdesign/auth';
import { type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  ContentDetailsGlobalStore,
  ContentDetailsService,
} from '@bizzdesign/data-access-content-details';
import {
  type PopulationBasicInfo,
  PopulationService,
} from '@bizzdesign/data-access-populations';
import type {
  ContentDetailsParams,
  FilterOption,
  NlsFeedback,
  SearchParameters,
  SearchResultItem,
} from '@bizzdesign/data-access-search';
import {
  buildViewMenuOptions,
  buildWorkspaceSearchMenuOptions,
  type ViewItem,
  type ViewMenuHandlers,
  type WorkspaceItem,
  type WorkspaceSearchMenuHandlers,
  WorkspacesService,
  type WorkspaceUpdate,
} from '@bizzdesign/data-access-workspaces';
import { AdvancedFilterComponent } from '@bizzdesign/feature-advanced-filter';
import { ContentDetailsContainerComponent } from '@bizzdesign/feature-content-details';
import {
  ContentOpenContainerComponent,
  type ContentOpenDialogChoice,
  ContentOpenService,
  newWorkspace,
} from '@bizzdesign/feature-content-open';
import { WorkspaceInviteContainerComponent } from '@bizzdesign/feature-invite';
import { BDCore } from '@bizzdesign/sdk-knowledgegraph';
import {
  BDCoreMetaModelDefinition,
  type ObjectType,
} from '@bizzdesign/sdk-knowledgegraph/bdcore';
import { type SearchScope, type SearchSortInput } from '@bizzdesign/sdk-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type ActionButton,
  type BDMenuItem,
  type ContentDetailsConfiguration,
  DefaultDialogSize,
  DialogProperties,
  type Scope,
  type StatusDialogSettings,
  type ToastActionButton,
} from '@bizzdesign/shared/models';
import { HighlightTextPipe } from '@bizzdesign/shared/pipes';
import { ProductAnalyticsService } from '@bizzdesign/shared/services';
import { baseRoutes, getBDCoreObjectTypeIcon } from '@bizzdesign/shared/utils';
import { BadgeComponent } from '@bizzdesign/ui-badge';
import { DialogComponent, DrawerDirective } from '@bizzdesign/ui-components';
import { PageHeaderComponent } from '@bizzdesign/ui-page-header';
import { RichMenuComponent } from '@bizzdesign/ui-rich-menu';
import { SnackbarToastComponent } from '@bizzdesign/ui-snackbar';
import { StatusDialogComponent } from '@bizzdesign/ui-status-dialog';
import { TranslocoService } from '@jsverse/transloco';
import { isEmpty, isEqual } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { TabsModule } from 'primeng/tabs';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  EMPTY,
  firstValueFrom,
  from,
  map,
  mergeMap,
  type Observable,
  of,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AllSearchResultsComponent } from '../../components/all-search-results/all-search-results.component';
import { CreatePopulationModalComponent } from '../../components/create-population-modal/create-population-modal.component';
import { EditDataDialogComponent } from '../../components/edit-data-dialog/edit-data-dialog.component';
import { NlsOverviewAndFeedbackComponent } from '../../components/nls-overview-and-feedback/nls-overview-and-feedback.component';
import { ObjectSearchResultsComponent } from '../../components/object-search-results/object-search-results.component';
import { ObjectSearchResultsComponentStore } from '../../components/object-search-results/object-search-results.component.store';
import { QuickFiltersComponent } from '../../components/quick-filters/quick-filters.component';
import { SearchbarComponent } from '../../components/searchbar/searchbar.component';
import { ViewSearchResultsComponent } from '../../components/view-search-results/view-search-results.component';
import { WorkspaceSearchResultsComponent } from '../../components/workspace-search-results/workspace-search-results.component';
import {
  getPopulationToastAction,
  getSearchHeaderAction,
} from '../../utils/search-action.utils';
import {
  applyScopeFilterToCounts,
  crossScopeEmptyStateLinks,
  resolveSingleResultTabSelection,
  shouldCloseDetailsOnTabChange,
} from '../../utils/search-tab.utils';
import { InteractiveAppliedFiltersContainerComponent } from '../interactive-applied-filters/interactive-applied-filters-container.component';
import { SearchContainerComponentStore } from '../search-container/search-container.component.store';
import { buildRefineWithCoworkerPrompt } from './refine-with-coworker.prompt';

const LINK_COPIED_SNACKBAR_KEY = 'search-link-copied-snackbar';

/**
 * Coordinates search state and renders the search results experience.
 *
 * @remarks Container component used by the home and layout features. The `context`
 * input selects where the search UI is rendered, such as the home or header view.
 */
@Component({
  selector: 'bd-split-search-container',
  imports: [
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    AdvancedFilterComponent,
    ButtonModule,
    ContentDetailsContainerComponent,
    SearchbarComponent,
    QuickFiltersComponent,
    InteractiveAppliedFiltersContainerComponent,
    ContentOpenContainerComponent,
    WorkspaceInviteContainerComponent,
    PageHeaderComponent,
    SharedI18nModule,
    RichMenuComponent,
    SnackbarToastComponent,
    DrawerDirective,
    CreatePopulationModalComponent,
    DialogComponent,
    ProgressSpinner,
    SkeletonModule,
    HighlightTextPipe,
    TabsModule,
    NlsOverviewAndFeedbackComponent,
    EditDataDialogComponent,
    AllSearchResultsComponent,
    ObjectSearchResultsComponent,
    ViewSearchResultsComponent,
    WorkspaceSearchResultsComponent,
    BadgeComponent,
    StatusDialogComponent,
  ],
  providers: [SearchContainerComponentStore, ObjectSearchResultsComponentStore],
  templateUrl: './split-search-container.component.html',
  styleUrl: './split-search-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitSearchContainerComponent implements OnInit, OnDestroy {
  private readonly contentOpenService = inject(ContentOpenService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly populationService = inject(PopulationService);
  private readonly workspacesService = inject(WorkspacesService);
  private readonly contentDetailsService = inject(ContentDetailsService);
  private readonly contentDetailsGlobalStore = inject(
    ContentDetailsGlobalStore,
  );
  private readonly messageService = inject(MessageService);
  private readonly productAnalyticsService = inject(ProductAnalyticsService);
  readonly translocoService = inject(TranslocoService);
  readonly store = inject(SearchContainerComponentStore);
  readonly aiAssistantV2Store = inject(AiAssistantV2Store);
  // Selection lives in one store shared by the Objects tab and the All-tab
  // object preview, so ticking a box on either surface updates the same set.
  readonly objectSelectionStore = inject(ObjectSearchResultsComponentStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accessControlService = inject(AccessControlService);

  private skipNextFeedbackToast = false;
  private observer?: ResizeObserver;

  @Input() set context(context: string) {
    this.store.updateContext(context);
    if (context === 'default') {
      this.store.clearSearch(true);
    }
  }

  filtersHeight = signal(0);
  readonly splitTabsOccupiedHeight = signal(90);
  readonly allTabValue = 0;
  readonly objectTabValue = 1;
  readonly viewTabValue = 2;
  readonly workspaceTabValue = 3;
  readonly activeTabValue = signal(this.allTabValue);
  // ContentItem.scope is the lowercase Scope, not the uppercase SearchScope below; the All
  // tab is omitted so it accepts every scope and never closes the details drawer.
  private readonly tabScope: Partial<Record<number, Scope>> = {
    [this.objectTabValue]: 'object',
    [this.viewTabValue]: 'view',
    [this.workspaceTabValue]: 'workspace',
  };
  readonly searchScopes = {
    object: 'OBJECT',
    view: 'VIEW',
    workspace: 'WORKSPACE',
  } as const satisfies Record<string, SearchScope>;
  private readonly scopeTabValues: Record<SearchScope, number> = {
    OBJECT: this.objectTabValue,
    VIEW: this.viewTabValue,
    WORKSPACE: this.workspaceTabValue,
  };

  readonly anyFilterApplied = computed(
    () => this.store.filterStoreProperties().appliedFilters.length > 0,
  );

  // Object-scoped filters exclude views and workspaces from the result queries,
  // but the count query still counts them by term; reconcile so badges,
  // auto-select and empty-state links match what the tabs actually show. In
  // AI/NLS mode the search is object-only and its count comes from the cypher
  // result, so views and workspaces collapse to zero.
  private readonly scopedResultCounts = computed<Map<SearchScope, number>>(
    () => {
      if (this.store.filterStoreProperties().aiSearchResultsActive) {
        return new Map<SearchScope, number>([
          [
            this.searchScopes.object,
            this.store.searchResultsStoreProperties().totalObjectsCount,
          ],
          [this.searchScopes.view, 0],
          [this.searchScopes.workspace, 0],
        ]);
      }
      return applyScopeFilterToCounts(
        this.store.filterStoreProperties().searchResultsCountPerScope,
        this.store.filterStoreProperties().advancedFilter.scopeFilters,
      );
    },
  );

  readonly scopesWithResults = computed<SearchScope[]>(() =>
    [...this.scopedResultCounts().entries()]
      .filter(([, count]) => count > 0)
      .map(([scope]) => scope),
  );

  // The empty-state cross-links are a cross-scope concern, so the container
  // decides them; each results tab just renders the list it is given.
  readonly objectEmptyStateLinks = computed(() =>
    crossScopeEmptyStateLinks(
      this.searchScopes.object,
      this.scopesWithResults(),
    ),
  );
  readonly viewEmptyStateLinks = computed(() =>
    crossScopeEmptyStateLinks(this.searchScopes.view, this.scopesWithResults()),
  );
  readonly workspaceEmptyStateLinks = computed(() =>
    crossScopeEmptyStateLinks(
      this.searchScopes.workspace,
      this.scopesWithResults(),
    ),
  );

  // Evaluated once per distinct set of loaded counts, and never after a manual
  // tab pick in the same search, so the user is never pulled off a tab they
  // chose. Keyed on the counts' contents (not the map reference) so it also
  // fires for an AI search, whose object-only counts come from the cypher
  // result rather than a new per-scope count map.
  private lastAutoSelectedCountsKey: string | undefined;
  private userSelectedTabForCurrentSearch = false;
  private previousSearchParams: SearchParameters | undefined;
  private readonly autoSelectSingleResultTab = effect(() => {
    if (
      this.store.context() !== 'search' ||
      this.store.searchResultsCountsPerScopeLoading()
    ) {
      return;
    }
    const scopedCounts = this.scopedResultCounts();
    const { processedKey, scopeToSelect } = resolveSingleResultTabSelection({
      scopedCounts,
      lastProcessedKey: this.lastAutoSelectedCountsKey,
      userSelectedTab: this.userSelectedTabForCurrentSearch,
      advancedFilterVisible:
        this.store.filterStoreProperties().advancedFilterVisible,
    });
    this.lastAutoSelectedCountsKey = processedKey;
    if (scopeToSelect === null) {
      return;
    }
    const targetTabValue = this.scopeTabValues[scopeToSelect];
    if (targetTabValue !== this.activeTabValue()) {
      untracked(() => this.selectTab(targetTabValue));
    }
  });

  // An AI/NLS submit changes the smart-search query, not searchParams, so it
  // bypasses the searchParams$ subscription. Mirror that reset here: allow the
  // object-only auto-select to fire again and drop stale view/workspace results.
  private lastAiSearchQuery: string | undefined;
  private readonly resetOnAiSearch = effect(() => {
    const aiSearchQuery = this.store.filterStoreProperties().smartSearchQuery;
    if (!this.store.aiSearchToggleOn() || aiSearchQuery.trim() === '') {
      // Forget the last query so re-running it after AI is toggled off/on (or
      // the query cleared) still triggers the reset instead of being deduped.
      this.lastAiSearchQuery = undefined;
      return;
    }
    if (aiSearchQuery === this.lastAiSearchQuery) {
      return;
    }
    this.lastAiSearchQuery = aiSearchQuery;
    untracked(() => {
      this.userSelectedTabForCurrentSearch = false;
      this.store.clearViewAndWorkspaceSearchResults();
    });
  });

  @ViewChild('filters')
  set elRef(value: ElementRef | undefined) {
    if (value) {
      this.observer?.disconnect();
      this.filtersHeight.set(value.nativeElement.offsetHeight);
      this.observer = new ResizeObserver((entries) => {
        const h = entries[0].contentRect.height;
        this.filtersHeight.set(h);
      });

      this.observer.observe(value.nativeElement);
    }
  }

  @ViewChild('createPopulationMenu') createPopulationMenu:
    | RichMenuComponent
    | undefined;
  @ViewChild('filters', { read: ElementRef })
  filtersElementRef: ElementRef<HTMLElement> | undefined;

  allElementTypeFilters: FilterOption<ObjectType>[] = Object.values(
    BDCore.objectDefinitions,
  ).map((objectDefinition) => {
    BDCoreMetaModelDefinition.assertIsObjectType(objectDefinition.key);
    return {
      key: objectDefinition.key,
      icon: getBDCoreObjectTypeIcon(objectDefinition.key),
      label: objectDefinition.label,
      checked: false,
    };
  });

  contentDetailsConfiguration: ContentDetailsConfiguration = {
    showPublicationStatusLabel: true,
    areDetailsEditable: true,
    areAttributesEditable: true,
  };

  private readonly searchParams$ = toObservable(this.store.searchParams);

  private readonly addObjectsToDisabled$ = toObservable(
    this.store.addObjectsToDisabled,
  );
  private readonly addObjectsToTooltipKey$ = toObservable(
    this.store.addObjectsToTooltipKey,
  );
  private readonly disableCreatePopulationButton$ = toObservable(
    this.store.disableCreatePopulationButton,
  );
  private readonly createPopulationButtonTooltipKey$ = toObservable(
    this.store.createPopulationButtonTooltipKey,
  );

  readonly headerActions = computed(() => {
    const actions: ActionButton[] = [];
    if (this.store.featureFlagsStore.addToFrameWithoutSelectionEnabled()) {
      actions.push({
        ...getSearchHeaderAction(
          'addObjectsToBtn',
          'addObjectsTo',
          'ui-plus',
          this.addObjectsToDisabled$,
          () => {
            const selection = this.store.contentSelection();
            if (selection) {
              this.store.showContentOpen(selection);
            }
          },
          this.addObjectsToTooltipKey$,
        ),
        subject: 'objects',
        actionKey: 'addToWorkspace',
      });
    }
    if (this.store.featureFlagsStore.populationsEnabled()) {
      actions.push(
        getSearchHeaderAction(
          'createPopulationBtn',
          'saveAsPopulation',
          'ui-plus',
          this.disableCreatePopulationButton$,
          (event) => this.createPopulationMenu?.menu?.toggle(event),
          this.createPopulationButtonTooltipKey$,
        ),
      );
    }
    return actions;
  });

  populationToastAction: ToastActionButton = getPopulationToastAction(() =>
    this.navigateToPopulationsOverview(),
  );

  readonly smartFilterLoadingDialogProperties: DialogProperties = {
    defaultHeader: { headerTitle: '' },
    defaultSize: DefaultDialogSize.SMALL,
  };

  readonly smartFiltersErrorDialogProperties: DialogProperties = {
    defaultHeader: { headerTitle: '' },
    defaultSize: DefaultDialogSize.SMALL,
  };

  private readonly workspaceFrameCountState$ = toObservable(
    this.store.workspaceSearchResultItems,
  ).pipe(
    switchMap((workspaceItems) => {
      const initial = {
        counts: {} as Record<string, number>,
        loading: Object.fromEntries(
          workspaceItems.map((workspaceItem) => [workspaceItem.id, true]),
        ),
      };
      if (workspaceItems.length === 0) {
        return of(initial);
      }
      return from(workspaceItems).pipe(
        mergeMap((workspaceItem) =>
          this.workspacesService.getFrameCount(workspaceItem.id).pipe(
            catchError(() => of(0)),
            map((count) => ({ id: workspaceItem.id, count })),
          ),
        ),
        scan(
          (state, { id, count }) => ({
            counts: { ...state.counts, [id]: count },
            loading: { ...state.loading, [id]: false },
          }),
          initial,
        ),
        startWith(initial),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly workspaceFrameCounts$ = this.workspaceFrameCountState$.pipe(
    map((state) => state.counts),
  );

  readonly workspaceFrameCountsLoading$ = this.workspaceFrameCountState$.pipe(
    map((state) => state.loading),
  );
  readonly workspaceFrameCounts = toSignal(this.workspaceFrameCounts$, {
    initialValue: {} as Record<string, number>,
  });
  readonly workspaceFrameCountsLoading = toSignal(
    this.workspaceFrameCountsLoading$,
    {
      initialValue: {} as Record<string, boolean>,
    },
  );
  private readonly workspaceUpdate$ = new Subject<WorkspaceUpdate>();
  readonly workspaceUpdateError$ = new Subject<unknown>();
  private readonly updatedWorkspace$ = this.workspaceUpdate$.pipe(
    concatMap((update) =>
      this.workspacesService.updateWorkspace(update).pipe(
        tap((updatedWorkspace) => {
          // Re-emitting the workspace lets the invite dialog end its in-flight state.
          if (this.workspaceToInvite()?.id === updatedWorkspace.id) {
            this.workspaceToInvite.set(updatedWorkspace);
          }
          if (update.archived === true) {
            this.pendingArchiveUpdate = null;
            this.archiveWorkspaceDialogVisible$.next(false);
            this.messageService.add({
              severity: 'success',
              summary: 'archiveToast.title',
              detail: 'archiveToast.details',
              data: { tScope: 'featureWorkspaces' },
            });
          } else if (update.archived === false) {
            this.messageService.add({
              severity: 'success',
              summary: 'unarchiveToast.title',
              detail: 'unarchiveToast.details',
              data: { tScope: 'featureWorkspaces' },
            });
          }
          this.store.getWorkspaceSearchResultItems(this.store.searchParams());
          // Badges read the separate count query, so refresh it or they go stale.
          this.store.updateSearchResultsCount();
        }),
        catchError((error: unknown) => {
          this.workspaceUpdateError$.next(error);
          this.messageService.add({
            severity: 'error',
            summary: 'updateError.title',
            detail: 'updateError.details',
            data: { tScope: 'featureWorkspaces' },
          });
          return EMPTY;
        }),
      ),
    ),
  );

  readonly archiveWorkspaceDialogVisible$ = new BehaviorSubject<boolean>(false);
  readonly archiveWorkspaceDialogProperties = new DialogProperties({
    defaultSize: DefaultDialogSize.SMALL,
  });
  private pendingArchiveUpdate: WorkspaceUpdate | null = null;
  pendingArchiveWorkspaceName = signal('');

  readonly copyViewToFrameDialogVisible$ = new BehaviorSubject<boolean>(false);
  private readonly viewToCopyToFrame = signal<ViewItem | undefined>(undefined);
  readonly deleteViewDialogVisible = signal(false);
  readonly viewToDelete = signal<ViewItem | undefined>(undefined);
  readonly deleteViewInProgress = signal(false);
  private readonly viewDrawerMenuHandlers: ViewMenuHandlers = {
    onPreview: (viewItem) => this.onOpenView(viewItem),
    onSeeDetails: (viewItem) => this.onOpenViewDetails(viewItem),
    onCopyToFrame: (viewItem) => this.onCopyViewToFrame(viewItem),
    onCopyLink: (viewItem) => this.onCopyViewLink(viewItem),
    onDelete: (viewItem) => this.onDeleteView(viewItem),
  };
  private readonly workspaceDrawerMenuHandlers: WorkspaceSearchMenuHandlers = {
    onOpen: (workspaceItem) => this.onOpenWorkspace(workspaceItem),
    onSeeDetails: (workspaceItem) => this.onOpenWorkspaceDetails(workspaceItem),
    onInvite: (workspaceItem) => this.onInviteToWorkspace(workspaceItem),
    onCopyLink: (workspaceItem) => this.onCopyWorkspaceLink(workspaceItem),
    onArchive: (workspaceItem) => this.onArchiveWorkspace(workspaceItem),
    onUnarchive: (workspaceItem) => this.onUnarchiveWorkspace(workspaceItem),
  };
  readonly drawerActionMenuItems = computed<BDMenuItem[] | undefined>(() => {
    const contentItem = this.store.contentDetailsStoreProperties().contentItem;
    if (contentItem?.scope === 'view') {
      return this.getDrawerViewMenuItems(contentItem.id);
    }
    if (contentItem?.scope === 'workspace') {
      return this.getDrawerWorkspaceMenuItems(contentItem.id);
    }
    return undefined;
  });
  readonly deleteViewDialogSettings = computed<StatusDialogSettings>(() => ({
    display: this.deleteViewDialogVisible(),
    status: 'failure',
    header: {
      textKey: 'deleteViewDialog.header',
      tScope: 'featureSearch',
    },
    confirmLabel: {
      textKey: 'deleteViewDialog.confirmLabel',
      tScope: 'featureSearch',
    },
    confirmDisabled: this.deleteViewInProgress(),
    dismissLabel: {
      textKey: 'deleteViewDialog.dismissLabel',
      tScope: 'featureSearch',
    },
    dismissDisabled: this.deleteViewInProgress(),
  }));

  readonly inviteWorkspaceDialogVisible$ = new BehaviorSubject<boolean>(false);
  readonly workspaceToInvite = signal<WorkspaceItem | undefined>(undefined);
  readonly inviteWorkspaceDialogProperties$: Observable<DialogProperties> =
    toObservable(this.workspaceToInvite).pipe(
      switchMap((workspaceItem) =>
        this.translocoService
          .selectTranslate(
            'inviteModal.invitePeople',
            { workspaceName: workspaceItem?.name },
            'featureWorkspaces',
          )
          .pipe(map((headerTitle) => ({ defaultHeader: { headerTitle } }))),
      ),
    );

  private getDrawerViewMenuItems(
    contentItemId: string,
  ): BDMenuItem[] | undefined {
    const viewItem = this.store
      .viewSearchResultItems()
      .find((item) => item.viewId === contentItemId);
    if (!viewItem) {
      return undefined;
    }
    return buildViewMenuOptions(viewItem, this.viewDrawerMenuHandlers, {
      includeSeeDetails: false,
    });
  }

  private getDrawerWorkspaceMenuItems(
    contentItemId: string,
  ): BDMenuItem[] | undefined {
    const workspaceItem = this.store
      .workspaceSearchResultItems()
      .find((item) => item.id === contentItemId);
    if (!workspaceItem) {
      return undefined;
    }
    return buildWorkspaceSearchMenuOptions(
      workspaceItem,
      this.workspaceDrawerMenuHandlers,
      { includeSeeDetails: false },
    );
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event): void {
    if (this.store.numberOfUnsavedChanges() > 0) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  ngOnInit(): void {
    this.updateSearchUrlParamsFromQueryParams();
    this.updatedWorkspace$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    // A new search reloads the results for whichever tab is currently active.
    this.searchParams$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((searchParams) => {
        const onlyObjectSortingChanged =
          this.onlyObjectSortingChanged(searchParams);
        this.previousSearchParams = searchParams;
        if (onlyObjectSortingChanged) {
          this.store.refreshObjectSearchResultItems(searchParams);
          return;
        }
        this.userSelectedTabForCurrentSearch = false;
        this.loadAllSearchResults(searchParams);
      });
  }

  onTabChange(value: string | number | undefined): void {
    if (value === undefined) {
      return;
    }
    const targetTabValue = Number(value);
    this.closeDetailsDrawerIfItemNotOnTab(targetTabValue);
    this.userSelectedTabForCurrentSearch = true;
    this.selectTab(targetTabValue);
  }

  private closeDetailsDrawerIfItemNotOnTab(targetTabValue: number): void {
    const shouldClose = shouldCloseDetailsOnTabChange(
      this.tabScope[targetTabValue],
      this.store.contentDetailsStoreProperties().contentItem?.scope,
      this.store.rightDrawerOpen(),
    );
    if (shouldClose) {
      this.store.updateIsDrawerOpen(false);
    }
  }

  onSwitchToScope(scope: SearchScope): void {
    this.onTabChange(this.scopeTabValues[scope]);
  }

  private selectTab(value: number): void {
    this.activeTabValue.set(value);
    this.retryFailedResultLoads(value);
  }

  // A cypher AI search — a live smart-cypher query (feature flag on) or a saved
  // cypher search restored from the URL — supplies its own object results and
  // totals, so the keyword loaders must stay off or they'd clobber them.
  // Filter-based AI search (flag off) does not: it applies smart filters and
  // relies on the normal object loader.
  private aiCypherResultsActive(): boolean {
    return (
      this.store.aiSearchToggleOn() &&
      (this.store.featureFlagsStore.aiSearchCypherQueryEnabled() ||
        !!this.store.cypherSearchId())
    );
  }

  // All scopes preload once per search at full page size, so tab switches
  // never refetch; the All-tab panels slice their previews from the same data.
  private loadAllSearchResults(searchParams = this.store.searchParams()): void {
    if (this.store.context() !== 'search') {
      return;
    }

    // Cypher AI search owns the object results; skip the keyword loaders.
    if (this.aiCypherResultsActive()) {
      return;
    }

    this.store.getObjectSearchResultItems(searchParams);

    // AI/NLS search is object-scoped, so views/workspaces stay empty (they are
    // reset by resetOnAiSearch); only the object loader runs for filter mode.
    if (this.store.aiSearchToggleOn()) {
      return;
    }
    this.store.getViewSearchResultItems(searchParams);
    this.store.getWorkspaceSearchResultItems(searchParams);
  }

  private onlyObjectSortingChanged(searchParams: SearchParameters): boolean {
    if (this.previousSearchParams === undefined) {
      return false;
    }
    if (searchParams.sorting === this.previousSearchParams.sorting) {
      return false;
    }
    const { sorting: _currentSorting, ...currentWithoutSorting } = searchParams;
    const { sorting: _previousSorting, ...previousWithoutSorting } =
      this.previousSearchParams;
    return isEqual(currentWithoutSorting, previousWithoutSorting);
  }

  private retryFailedResultLoads(tabValue: number): void {
    if (this.aiCypherResultsActive()) {
      return;
    }
    const searchParams = this.store.searchParams();
    const showsAllScopes = tabValue === this.allTabValue;
    if (
      (showsAllScopes || tabValue === this.objectTabValue) &&
      this.store.objectSearchResultsFailed()
    ) {
      this.store.getObjectSearchResultItems(searchParams);
    }
    // Filter-based AI search is object-only; no view/workspace loaders to retry.
    if (this.store.aiSearchToggleOn()) {
      return;
    }
    if (
      (showsAllScopes || tabValue === this.viewTabValue) &&
      this.store.viewSearchResultsFailed()
    ) {
      this.store.getViewSearchResultItems(searchParams);
    }
    if (
      (showsAllScopes || tabValue === this.workspaceTabValue) &&
      this.store.workspaceSearchResultsFailed()
    ) {
      this.store.getWorkspaceSearchResultItems(searchParams);
    }
  }

  onOpenWorkspace(workspaceItem: WorkspaceItem): void {
    this.contentOpenService.openWorkspace(workspaceItem.id, workspaceItem.name);
  }

  onOpenWorkspaceDetails(workspaceItem: WorkspaceItem): void {
    this.store.openWorkspaceDetails(workspaceItem);
  }

  onOpenView(viewItem: ViewItem): void {
    this.contentOpenService.openView(viewItem.viewId, viewItem.name);
  }

  onOpenViewDetails(viewItem: ViewItem): void {
    this.store.openViewDetails(viewItem);
  }

  onCopyViewToFrame(viewItem: ViewItem): void {
    this.viewToCopyToFrame.set(viewItem);
    this.copyViewToFrameDialogVisible$.next(true);
  }

  onDeleteView(viewItem: ViewItem): void {
    this.viewToDelete.set(viewItem);
    this.deleteViewDialogVisible.set(true);
  }

  onCopyViewToFrameConfirm(pickerChoice: ContentOpenDialogChoice): void {
    const viewItem = this.viewToCopyToFrame();
    if (!viewItem) {
      return;
    }

    if (pickerChoice.selectedWorkspace === newWorkspace) {
      this.contentOpenService.openViewInNewWorkspace(viewItem.viewId);
    } else {
      this.contentOpenService.openViewInWorkspace(
        viewItem.viewId,
        pickerChoice.selectedWorkspace,
      );
    }
  }

  async onCopyViewLink(viewItem: ViewItem): Promise<void> {
    await this.copyLinkToClipboard(
      `${window.location.origin}/${baseRoutes.views}/${viewItem.viewId}`,
    );
  }

  async onDeleteViewConfirm(): Promise<void> {
    if (this.deleteViewInProgress()) {
      return;
    }
    const viewItem = this.viewToDelete();
    if (!viewItem) {
      return;
    }
    this.deleteViewInProgress.set(true);
    try {
      const result = await firstValueFrom(
        this.contentDetailsService.deleteView(viewItem.viewId),
      );
      if (!result) {
        this.showDeleteViewError(
          'deleteViewDialog.errorTitle',
          'deleteViewDialog.errorDescription',
        );
        return;
      }
      const deleteViewItemResult = await firstValueFrom(
        this.workspacesService.deleteViewItem(viewItem),
      );
      this.onDeleteViewDismiss();
      this.store.updateIsDrawerOpen(false);
      this.store.getViewSearchResultItems(this.store.searchParams());
      this.store.updateSearchResultsCount();
      if (deleteViewItemResult instanceof Error) {
        this.showDeleteViewError(
          'deleteViewDialog.partialCleanupErrorTitle',
          'deleteViewDialog.partialCleanupErrorDescription',
        );
      }
    } finally {
      this.deleteViewInProgress.set(false);
    }
  }

  onDeleteViewDismiss(): void {
    this.deleteViewDialogVisible.set(false);
    this.viewToDelete.set(undefined);
  }

  async onCopyWorkspaceLink(workspaceItem: WorkspaceItem): Promise<void> {
    await this.copyLinkToClipboard(
      `${window.location.origin}/${baseRoutes.workspaces}/${workspaceItem.id}`,
    );
  }

  private async copyLinkToClipboard(link: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
      this.messageService.add({
        key: LINK_COPIED_SNACKBAR_KEY,
        severity: 'snackbar',
        life: 3000,
        data: {
          showProgressSpinner: false,
          icon: 'ui-check',
          labelKey: 'snackbar.linkCopied',
          tScope: 'featureWorkspaces',
        },
      });
    } catch (error) {
      console.error('Failed to copy link to clipboard', error);
      this.messageService.add({
        severity: 'error',
        summary: 'copyLinkError.title',
        detail: 'copyLinkError.details',
        data: { tScope: 'featureWorkspaces' },
      });
    }
  }

  private showDeleteViewError(summary: string, detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary,
      detail,
      data: { tScope: 'featureSearch' },
    });
  }

  onInviteToWorkspace(workspaceItem: WorkspaceItem): void {
    this.workspaceToInvite.set(workspaceItem);
    this.inviteWorkspaceDialogVisible$.next(true);
  }

  onInviteWorkspaceUpdate(workspaceUpdate: WorkspaceUpdate): void {
    this.workspaceUpdate$.next(workspaceUpdate);
  }

  onArchiveWorkspace(workspaceItem: WorkspaceItem): void {
    this.onWorkspaceUpdate({ id: workspaceItem.id, archived: true });
  }

  onUnarchiveWorkspace(workspaceItem: WorkspaceItem): void {
    this.onWorkspaceUpdate({ id: workspaceItem.id, archived: false });
  }

  onWorkspaceUpdate(workspaceUpdate: WorkspaceUpdate): void {
    if (workspaceUpdate.archived === true) {
      this.pendingArchiveUpdate = workspaceUpdate;
      this.pendingArchiveWorkspaceName.set(
        this.store
          .workspaceSearchResultItems()
          .find((w) => w.id === workspaceUpdate.id)?.name ?? '',
      );
      this.archiveWorkspaceDialogVisible$.next(true);
      return;
    }

    this.workspaceUpdate$.next(workspaceUpdate);
  }

  onArchiveWorkspaceConfirm(): void {
    if (this.pendingArchiveUpdate) {
      // Keep the pending update so a failed archive stays retryable while the
      // dialog remains open; it is cleared once the update succeeds.
      this.workspaceUpdate$.next(this.pendingArchiveUpdate);
    }
  }

  onArchiveWorkspaceCancel(): void {
    this.pendingArchiveUpdate = null;
    this.pendingArchiveWorkspaceName.set('');
    this.archiveWorkspaceDialogVisible$.next(false);
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  get manageWorkspaceNoAccessTooltip(): string {
    const tooltipKey = this.store.manageWorkspaceNoAccessKey();
    return tooltipKey ? this.translocoService.translate(tooltipKey) : '';
  }

  getSearchResultsCountForScope(scope: SearchScope): number {
    return this.scopedResultCounts().get(scope) ?? 0;
  }

  isScopeFilterApplied(scope: SearchScope): boolean {
    return this.store
      .filterStoreProperties()
      .advancedFilter.scopeFilters.includes(scope);
  }

  onOpenContentDetails(contentDetailsParams: ContentDetailsParams) {
    this.store.updateContentItem(contentDetailsParams.item);
    this.store.updateIsDrawerOpen(true);
    this.store.updateScrollToUsedIn(!!contentDetailsParams.scrollToUsedIn);
  }

  onDeleteSearchResultItem(item: SearchResultItem): void {
    this.store.updateContentItem(item);
    this.store.updateIsDrawerOpen(true);
    this.store.updateScrollToUsedIn(false);
    this.contentDetailsGlobalStore.updateDeleteObjectDialogVisible(true);
  }

  onOpenSearchResultItem(item: SearchResultItem): void {
    if (item.scope === 'view') {
      this.contentOpenService.openView(item.id, item.name);
    } else if (item.scope === 'workspace') {
      this.contentOpenService.openWorkspace(item.id, item.name);
    } else if (item.scope === 'object') {
      this.store.showContentOpen({
        selectedItems: [item],
      });
    }
  }

  onAllTabItemSelectionToggled(event: {
    shiftKey: boolean;
    itemId: SearchResultItem['id'];
  }): void {
    this.objectSelectionStore.updateToggledItemIndexes(
      event.shiftKey,
      event.itemId,
    );
  }

  onAllTabSelectedItemsChange(selectedItems: SearchResultItem[]): void {
    this.objectSelectionStore.updateSelectedItems(selectedItems);
    if (this.objectSelectionStore.selectAllApplied()) {
      this.objectSelectionStore.updateUnselectedItemsFromCurrentSelection();
    }
    this.store.onObjectSelectionChange(
      this.objectSelectionStore.getObjectSelection(),
    );
  }

  onCancelContentOpen(): void {
    this.store.cancelObjectLoading$.next(true);
  }

  onSaveAsPopulation(populationInfo: PopulationBasicInfo) {
    this.store.cancelObjectLoading$.next(false);
    this.store.updateDataForPopulationLoading(true);
    this.store
      .updateSelectedElements(this.store.cancelObjectLoading$)
      .subscribe(() => {
        const selectedContent = this.store.selectedObjects();

        if (selectedContent) {
          this.populationService
            .savePopulation(
              populationInfo,
              this.store.filterStoreProperties().advancedFilter,
              selectedContent ? selectedContent.map((o) => o.id) : [],
            )
            .pipe(
              tap((_population) => {
                this.store.updateDataForPopulationLoading(false);
                this.store.updateCreatePopulationModalVisible(false);
                this.store.updateIsSelectionMode(false);
                this.store.onObjectSelectionChange({
                  selectedItems: [],
                  selectedItemsCount: 0,
                });

                this.messageService.add({
                  severity: 'success',
                  summary: 'populationSavedToast.title',
                  detail: 'populationSavedToast.details',
                  data: {
                    toastAction: this.populationToastAction,
                    tScope: 'featureSearch',
                  },
                });
              }),
            )
            .subscribe();
        } else {
          this.store.updateIsSelectionMode(false);
        }
      });
  }

  onCancelSaveAsPopulation(): void {
    this.store.cancelObjectLoading$.next(true);
  }

  onAiSearchChange(isAiSearch: boolean): void {
    this.store.updateAiSearchToggleOn(isAiSearch);
  }

  navigateToPopulationsOverview(): void {
    this.messageService.clear();
    this.router.navigate([baseRoutes.populations]);
  }

  // Toggling an already-open panel would close it, so check first.
  openAiCoworker(): void {
    if (!this.aiAssistantV2Store.isAssistantPanelVisible()) {
      this.aiAssistantV2Store.toggleAssistant();
    }

    const query = this.store.filterStoreProperties().smartSearchQuery;
    if (isNotBlank(query)) {
      void this.aiAssistantV2Store.sendMessage(
        buildRefineWithCoworkerPrompt(query),
      );
    }
  }

  onClearFiltersClicked(): void {
    this.clearFilters();
  }

  onNoResultsClearFiltersClicked(): void {
    this.store.trackNoResultsClearFiltersClick();
    this.clearFilters();
  }

  private clearFilters(): void {
    this.skipNextFeedbackToast = true;
    this.closeAiCoworker();
    this.store.clearFilters();
  }

  private closeAiCoworker(): void {
    if (this.aiAssistantV2Store.isAssistantPanelVisible()) {
      this.aiAssistantV2Store.toggleAssistant();
    }
  }

  onFeedbackSent(feedback: NlsFeedback): void {
    if (this.skipNextFeedbackToast) {
      this.skipNextFeedbackToast = false;
      return;
    }

    const hasComment = !isEmpty(feedback.comment?.trim());

    const messageKey = (() => {
      if (hasComment) {
        return 'nlsOverviewAndFeedback.feedbackToastCommentMessage';
      }
      if (feedback.isPositive) {
        return 'nlsOverviewAndFeedback.feedbackToastPositiveMessage';
      }
      return 'nlsOverviewAndFeedback.feedbackToastNegativeMessage';
    })();

    this.messageService.add({
      severity: 'success',
      summary: 'nlsOverviewAndFeedback.feedbackToastTitle',
      detail: messageKey,
      data: { tScope: 'featureSearch' },
    });
  }

  onCancelSmartFiltersLoading(): void {
    this.store.updateCancelSmartFiltersLoading(true);
  }

  retrySmartQuery(): void {
    this.store.updateSmartFiltersErrorDialogVisibility(false);
    this.store.toggleSmartFiltersForceRetry();
    this.store.updateSmartFiltersRefineQuery(false);
  }

  refineSmartQuery(): void {
    this.store.updateSmartFiltersErrorDialogVisibility(false);
    this.store.updateSmartFiltersRefineQuery(true);
  }

  onPageChange(pageNumber: number): void {
    this.store.updateIsDrawerOpen(false);
    this.store.getSearchResultItemsPage(pageNumber - 1);
  }

  onObjectSortChange(sorting: SearchSortInput | undefined): void {
    this.store.updateIsDrawerOpen(false);
    this.store.updateObjectResultsSorting(sorting);
  }

  onWorkspacePageChange(pageNumber: number): void {
    this.store.updateIsDrawerOpen(false);
    this.store.getWorkspaceSearchResultItemsPage(pageNumber - 1);
  }

  onViewPageChange(pageNumber: number): void {
    this.store.updateIsDrawerOpen(false);
    this.store.getViewSearchResultItemsPage(pageNumber - 1);
  }

  onDismissEditData(): void {
    this.store.closeEditDataWarningDialog();
  }

  onConfirmEditData(objectIds: UUID[]): void {
    this.store.closeEditDataWarningDialog();
    if (isEmpty(objectIds)) {
      return;
    }
    this.store.navigateToManageData(objectIds);
  }

  cancelEditDataLoading(): void {
    this.store.cancelObjectLoading$.next(true);
    this.store.updateEditDataLoading(false);
  }

  onSubmitSearch(searchTerm: string): void {
    this.store.handleSearch(searchTerm);
    this.productAnalyticsService.sendEvent('search_triggered_from_searchbar');
  }

  updateSearchUrlParamsFromQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.store.updateSearchUrlParamsFromQueryParams(params);
      });
  }

  onContentItemAddTo(itemId: string): void {
    this.store.updateDrawerAddToObjectId(itemId);
    this.store.updateContentOpenDialogVisible(true);
  }
}
